import React, { useState, useRef } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  Chip,
  Avatar,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Share as ShareIcon,
  Chat as WechatIcon,
  ChatBubble as WeiboIcon,
  ChatBubble as QQIcon,
  Image as XiaohongshuIcon,
  VideoLibrary as DouyinIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  QrCode as QrCodeIcon,
  Download as DownloadIcon,
  Print as PrintIcon
} from '@mui/icons-material';

const ShareButtonAdvanced = ({ 
  title = '分享内容',
  url = window.location.href,
  description = '',
  image = '',
  hashtags = [],
  children,
  variant = 'outlined',
  size = 'medium',
  showQRCode = true,
  showPrint = true,
  showDownload = false,
  platforms = ['wechat', 'weibo', 'qq', 'qzone', 'xiaohongshu', 'douyin', 'copy'],
  customPlatforms = [],
  onShare = null,
  ...props 
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const qrCodeRef = useRef(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleQrDialogClose = () => {
    setQrDialogOpen(false);
  };

  const handleLinkDialogClose = () => {
    setLinkDialogOpen(false);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // 生成分享链接
  const generateShareUrl = (platform, shareData) => {
    const { title, url, description, image, hashtags } = shareData;
    const encodedTitle = encodeURIComponent(title);
    const encodedUrl = encodeURIComponent(url);
    const encodedDescription = encodeURIComponent(description);
    const encodedImage = encodeURIComponent(image);
    const encodedHashtags = hashtags ? hashtags.map(tag => `#${tag}`).join(' ') : '';

    const shareUrls = {
      weibo: `https://service.weibo.com/share/share.php?url=${encodedUrl}&title=${encodedTitle}&pic=${encodedImage}&content=${encodedDescription}`,
      qq: `https://connect.qq.com/widget/shareqq/index.html?url=${encodedUrl}&title=${encodedTitle}&summary=${encodedDescription}&pics=${encodedImage}`,
      qzone: `https://sns.qzone.qq.com/cgi-bin/qzshare/cgi_qzshare_onekey?url=${encodedUrl}&title=${encodedTitle}&desc=${encodedDescription}&pics=${encodedImage}`,
      douban: `https://www.douban.com/share/service?href=${encodedUrl}&name=${encodedTitle}&text=${encodedDescription}&image=${encodedImage}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}&hashtags=${encodedHashtags}`
    };

    return shareUrls[platform] || url;
  };

  // 复制到剪贴板
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showSnackbar('链接已复制到剪贴板');
    } catch (err) {
      // 降级方案
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showSnackbar('链接已复制到剪贴板');
    }
  };

  // 分享处理函数
  const handleShare = (platform, action) => {
    if (onShare) {
      onShare(platform, { title, url, description, image, hashtags });
    }
    action();
  };

  // 分享到微信
  const shareToWechat = () => {
    setQrDialogOpen(true);
    handleClose();
  };

  // 分享到微博
  const shareToWeibo = () => {
    const shareData = { title, url, description, image, hashtags };
    const shareUrl = generateShareUrl('weibo', shareData);
    window.open(shareUrl, '_blank', 'width=600,height=400');
    handleClose();
  };

  // 分享到QQ
  const shareToQQ = () => {
    const shareData = { title, url, description, image, hashtags };
    const shareUrl = generateShareUrl('qq', shareData);
    window.open(shareUrl, '_blank', 'width=600,height=400');
    handleClose();
  };

  // 分享到QQ空间
  const shareToQzone = () => {
    const shareData = { title, url, description, image, hashtags };
    const shareUrl = generateShareUrl('qzone', shareData);
    window.open(shareUrl, '_blank', 'width=600,height=400');
    handleClose();
  };

  // 分享到小红书
  const shareToXiaohongshu = () => {
    setLinkDialogOpen(true);
    handleClose();
  };

  // 分享到抖音
  const shareToDouyin = () => {
    setLinkDialogOpen(true);
    handleClose();
  };

  // 复制链接
  const copyLink = () => {
    copyToClipboard(url);
    handleClose();
  };

  // 打印页面
  const printPage = () => {
    window.print();
    handleClose();
  };

  // 下载页面（生成PDF）
  const downloadPage = () => {
    // 这里可以实现PDF生成功能
    showSnackbar('下载功能开发中...', 'info');
    handleClose();
  };

  // 生成二维码
  const generateQRCode = () => {
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`;
  };

  // 默认平台配置
  const defaultPlatforms = {
    wechat: {
      key: 'wechat',
      label: '微信',
      icon: <WechatIcon />,
      action: shareToWechat,
      color: '#07C160',
      description: '扫描二维码分享到微信'
    },
    weibo: {
      key: 'weibo',
      label: '微博',
      icon: <WeiboIcon />,
      action: shareToWeibo,
      color: '#E6162D',
      description: '分享到新浪微博'
    },
    qq: {
      key: 'qq',
      label: 'QQ',
      icon: <QQIcon />,
      action: shareToQQ,
      color: '#12B7F5',
      description: '分享到QQ好友'
    },
    qzone: {
      key: 'qzone',
      label: 'QQ空间',
      icon: <QQIcon />,
      action: shareToQzone,
      color: '#12B7F5',
      description: '分享到QQ空间'
    },
    xiaohongshu: {
      key: 'xiaohongshu',
      label: '小红书',
      icon: <XiaohongshuIcon />,
      action: shareToXiaohongshu,
      color: '#FF2442',
      description: '复制链接到小红书分享'
    },
    douyin: {
      key: 'douyin',
      label: '抖音',
      icon: <DouyinIcon />,
      action: shareToDouyin,
      color: '#000000',
      description: '复制链接到抖音分享'
    },
    copy: {
      key: 'copy',
      label: '复制链接',
      icon: <CopyIcon />,
      action: copyLink,
      color: '#666666',
      description: '复制分享链接'
    }
  };

  // 合并自定义平台
  const allPlatforms = { ...defaultPlatforms, ...customPlatforms };

  // 根据配置过滤平台
  const shareOptions = platforms
    .map(platform => allPlatforms[platform])
    .filter(Boolean);

  // 添加额外功能
  const extraOptions = [];
  if (showQRCode) {
    extraOptions.push({
      key: 'qrcode',
      label: '二维码',
      icon: <QrCodeIcon />,
      action: () => {
        setQrDialogOpen(true);
        handleClose();
      },
      color: '#666666',
      description: '生成分享二维码'
    });
  }
  if (showPrint) {
    extraOptions.push({
      key: 'print',
      label: '打印',
      icon: <PrintIcon />,
      action: printPage,
      color: '#666666',
      description: '打印当前页面'
    });
  }
  if (showDownload) {
    extraOptions.push({
      key: 'download',
      label: '下载',
      icon: <DownloadIcon />,
      action: downloadPage,
      color: '#666666',
      description: '下载为PDF'
    });
  }

  const allOptions = [...shareOptions, ...extraOptions];

  return (
    <>
      {children ? (
        React.cloneElement(children, {
          onClick: handleClick,
          ...props
        })
      ) : (
        <Button
          variant={variant}
          size={size}
          startIcon={<ShareIcon />}
          onClick={handleClick}
          {...props}
        >
          分享
        </Button>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 250,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1.5
            }
          }
        }}
      >
        {allOptions.map((option, index) => (
          <MenuItem 
            key={option.key} 
            onClick={() => handleShare(option.key, option.action)}
          >
            <ListItemIcon sx={{ color: option.color, minWidth: 36 }}>
              {option.icon}
            </ListItemIcon>
            <ListItemText 
              primary={option.label}
              secondary={option.description}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* 微信分享二维码对话框 */}
      <Dialog open={qrDialogOpen} onClose={handleQrDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">微信分享</Typography>
            <IconButton onClick={handleQrDialogClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box textAlign="center" py={2}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              请使用微信扫描二维码分享
            </Typography>
            <Box
              ref={qrCodeRef}
              component="img"
              src={generateQRCode()}
              alt="分享二维码"
              sx={{
                width: 200,
                height: 200,
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                mb: 2
              }}
            />
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {title}
              </Typography>
              {description && (
                <Typography variant="body2" color="text.secondary">
                  {description}
                </Typography>
              )}
            </Box>
            {hashtags.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  标签：
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {hashtags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={`#${tag}`}
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleQrDialogClose}>关闭</Button>
        </DialogActions>
      </Dialog>

      {/* 复制链接对话框 */}
      <Dialog open={linkDialogOpen} onClose={handleLinkDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">复制分享链接</Typography>
            <IconButton onClick={handleLinkDialogClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box py={2}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              请复制以下链接到对应平台分享：
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={url}
              variant="outlined"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton
                    onClick={() => copyToClipboard(url)}
                    edge="end"
                    size="small"
                  >
                    <CopyIcon />
                  </IconButton>
                )
              }}
            />
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                分享内容：
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                标题：{title}
              </Typography>
              {description && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  描述：{description}
                </Typography>
              )}
              {hashtags.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    标签：
                  </Typography>
                  {hashtags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={`#${tag}`}
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => copyToClipboard(url)} startIcon={<CopyIcon />}>
            复制链接
          </Button>
          <Button onClick={handleLinkDialogClose}>关闭</Button>
        </DialogActions>
      </Dialog>

      {/* 提示消息 */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ShareButtonAdvanced;
